/**
 * Evolution Chart for IronHUD (Alpine.js Component)
 */

export const ChartComponent = (config = {}) => ({
    chart: null,
    labels: config.labels || [],
    weight: config.weight || [],
    bf: config.bf || [],
    weightLabel: config.weightLabel || 'Weight',
    bfLabel: config.bfLabel || 'Body Fat %',

    init() {
        // Wait for next tick to ensure $el is ready and Chart.js might have finished loading
        this.$nextTick(() => {
            this.tryRender();
        });

        // Re-render on theme change
        window.addEventListener('theme-changed', () => {
            if (this.chart) {
                this.chart.destroy();
                this.render();
            }
        });
    },

    tryRender(retries = 5) {
        if (typeof window.Chart !== 'undefined') {
            this.render();
        } else if (retries > 0) {
            setTimeout(() => this.tryRender(retries - 1), 100);
        } else {
            console.error('Chart.js failed to load after multiple retries.');
        }
    },

    destroy() {
        if (this.chart) this.chart.destroy();
    },

    render() {
        const el = this.$el;
        if (!el || this.labels.length === 0) return;

        // Destroy any stale Chart.js instance on this canvas (e.g. after HTMX swap)
        const existing = window.Chart?.getChart?.(el);
        if (existing) existing.destroy();

        const isDark = document.documentElement.classList.contains('dark');
        const colors = {
            primary: isDark ? '#60a5fa' : '#312e81',
            secondary: isDark ? '#fbbf24' : '#b45309',
            grid: isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)',
            text: isDark ? '#94a3b8' : '#64748b',
        };

        const ctx = el.getContext('2d');
        if (!ctx) return;

        const weightGradient = ctx.createLinearGradient(0, 0, 0, 400);
        weightGradient.addColorStop(
            0,
            isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(49, 46, 129, 0.05)'
        );
        weightGradient.addColorStop(1, 'transparent');

        this.chart = new window.Chart(el, {
            type: 'line',
            data: {
                labels: this.labels,
                datasets: [
                    {
                        label: this.weightLabel,
                        data: this.weight,
                        borderColor: colors.primary,
                        backgroundColor: weightGradient,
                        borderWidth: 4,
                        fill: true,
                        tension: 0.4,
                        yAxisID: 'y',
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: colors.primary,
                        pointHoverBorderColor: isDark ? '#0f172a' : '#fff',
                        pointHoverBorderWidth: 3,
                    },
                    {
                        label: this.bfLabel,
                        data: this.bf,
                        borderColor: colors.secondary,
                        backgroundColor: 'transparent',
                        borderWidth: 3,
                        borderDash: [5, 5],
                        tension: 0.4,
                        yAxisID: 'y1',
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: colors.secondary,
                        pointHoverBorderColor: isDark ? '#0f172a' : '#fff',
                        pointHoverBorderWidth: 3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        titleColor: isDark ? '#f8fafc' : '#0f172a',
                        bodyColor: isDark ? '#cbd5e1' : '#475569',
                        borderColor: isDark ? '#334155' : '#e2e8f0',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 12,
                        titleFont: { family: 'Inter', weight: 'bold' },
                        bodyFont: { family: 'Inter' },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            color: colors.text,
                            font: { family: 'Inter', size: 10, weight: 'bold' },
                        },
                    },
                    y: {
                        position: 'left',
                        grid: { color: colors.grid },
                        ticks: { color: colors.text, font: { family: 'Inter', size: 10 } },
                    },
                    y1: {
                        position: 'right',
                        grid: { display: false },
                        ticks: { color: colors.text, font: { family: 'Inter', size: 10 } },
                    },
                },
            },
        });
    },
});
